import type {
  CriticalResultsAlertCategory,
  CriticalResultsKpiKey,
} from '../types/criticalResultsDashboard.types';

export type CriticalResultsAlertColumnKey =
  | 'accession'
  | 'patient'
  | 'study'
  | 'criticality'
  | 'referringClinician'
  | 'level'
  | 'status'
  | 'sinceNotified'
  | 'ackTat';

export const CRITICAL_RESULTS_ACK_STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'Acknowledged', label: 'Acknowledged' },
  { value: 'Unacknowledged', label: 'Unacknowledged' },
] as const;

export const CRITICAL_RESULTS_ALERT_COLUMN_LABELS: Record<CriticalResultsAlertColumnKey, string> = {
  accession: 'Accession',
  patient: 'Patient',
  study: 'Study',
  criticality: 'Criticality',
  referringClinician: 'Referring clinician',
  level: 'Level',
  status: 'Status',
  sinceNotified: 'Since notified',
  ackTat: 'Ack TAT',
};

export const CRITICAL_RESULTS_ALERT_COLUMNS_DEFAULT_VISIBILITY: Record<
  CriticalResultsAlertColumnKey,
  boolean
> = {
  accession: true,
  patient: true,
  study: true,
  criticality: true,
  referringClinician: true,
  level: true,
  status: true,
  sinceNotified: true,
  ackTat: true,
};

export const CRITICAL_RESULTS_SORT_FIELDS = [
  'startedAt',
  'acknowledgedAt',
  'nextActionAt',
  'currentLevelNumber',
  'criticalityBand',
  'status',
] as const;

export type CriticalResultsSortField = (typeof CRITICAL_RESULTS_SORT_FIELDS)[number];

export const CRITICAL_RESULTS_COLUMN_SORT_FIELD: Partial<
  Record<CriticalResultsAlertColumnKey, CriticalResultsSortField>
> = {
  sinceNotified: 'startedAt',
  ackTat: 'acknowledgedAt',
  level: 'currentLevelNumber',
  status: 'status',
  criticality: 'criticalityBand',
};

export interface CriticalResultsDrillDownConfig {
  slug: string;
  title: string;
  category: CriticalResultsAlertCategory;
  kpiKey: CriticalResultsKpiKey;
  defaultSortBy: CriticalResultsSortField;
  defaultSortOrder: 'ASC' | 'DESC';
}

export const CRITICAL_RESULTS_DRILL_DOWN_CONFIGS: CriticalResultsDrillDownConfig[] = [
  {
    slug: 'open-alerts',
    title: 'Open Alerts',
    category: 'open',
    kpiKey: 'open',
    defaultSortBy: 'startedAt',
    defaultSortOrder: 'DESC',
  },
  {
    slug: 'pending-acknowledgements',
    title: 'Pending Acknowledgements',
    category: 'pendingAcknowledgement',
    kpiKey: 'pending',
    defaultSortBy: 'startedAt',
    defaultSortOrder: 'DESC',
  },
  {
    slug: 'escalated-alerts',
    title: 'Escalated Alerts',
    category: 'escalated',
    kpiKey: 'escalated',
    defaultSortBy: 'nextActionAt',
    defaultSortOrder: 'ASC',
  },
  {
    slug: 'sla-breaches',
    title: 'SLA Breaches',
    category: 'slaBreached',
    kpiKey: 'breaches',
    defaultSortBy: 'nextActionAt',
    defaultSortOrder: 'ASC',
  },
  {
    slug: 'acknowledged-alerts',
    title: 'Acknowledged',
    category: 'acknowledged',
    kpiKey: 'acknowledged',
    defaultSortBy: 'acknowledgedAt',
    defaultSortOrder: 'DESC',
  },
  {
    slug: 'beyond-sla-alerts',
    title: 'Beyond SLA',
    category: 'slaBreached',
    kpiKey: 'beyondSla',
    defaultSortBy: 'nextActionAt',
    defaultSortOrder: 'ASC',
  },
  {
    slug: 'cancelled-alerts',
    title: 'Cancelled / Archived Alerts',
    category: 'cancelled',
    kpiKey: 'cancelled',
    defaultSortBy: 'startedAt',
    defaultSortOrder: 'DESC',
  },
  {
    slug: 'delivery-failed-alerts',
    title: 'Delivery Failed',
    category: 'deliveryFailed',
    kpiKey: 'deliveryFailed',
    defaultSortBy: 'startedAt',
    defaultSortOrder: 'DESC',
  },
];

export const CRITICAL_RESULTS_DRILL_DOWN_BY_SLUG = Object.fromEntries(
  CRITICAL_RESULTS_DRILL_DOWN_CONFIGS.map(config => [config.slug, config])
) as Record<string, CriticalResultsDrillDownConfig>;

export const CRITICAL_RESULTS_KPI_DRILL_DOWN_PATH: Partial<Record<CriticalResultsKpiKey, string>> =
  Object.fromEntries(
    CRITICAL_RESULTS_DRILL_DOWN_CONFIGS.map(config => [
      config.kpiKey,
      `/critical-results-dashboard/${config.slug}`,
    ])
  );

export const CRITICAL_RESULTS_EXPORT_DATASET_BY_SLUG = {
  ...Object.fromEntries(
    CRITICAL_RESULTS_DRILL_DOWN_CONFIGS.map(config => [config.slug, config.slug])
  ),
  'beyond-sla-alerts': 'sla-breaches',
} as Record<string, string>;

export const CRITICAL_RESULTS_SUMMARY_EXPORT_DATASET = 'summary' as const;

export const CRITICAL_RESULTS_REFRESH_INTERVAL_OPTIONS = [
  { value: 30, label: '30 seconds' },
  { value: 60, label: '1 minute' },
  { value: 120, label: '2 minutes' },
  { value: 300, label: '5 minutes' },
] as const;

export const CRITICAL_RESULTS_DEFAULT_REFRESH_INTERVAL_SEC = 60;
export const CRITICAL_RESULTS_REFRESH_RETRY_MS = 15_000;

export const CRITICAL_RESULTS_FILTER_QUERY_KEYS = {
  startDate: 'from',
  endDate: 'to',
  facilityId: 'facilityId',
  department: 'department',
  criticalityBand: 'criticality',
  status: 'status',
  escalationLevel: 'level',
  referringClinician: 'clinician',
  autoRefresh: 'autoRefresh',
  refreshInterval: 'refreshInterval',
  page: 'page',
  sortBy: 'sortBy',
  sortOrder: 'sortOrder',
  search: 'search',
  columns: 'columns',
} as const;

export const CRITICAL_RESULTS_LIST_PAGE_SIZE = 10;
